import { Controller, useFormContext, useWatch } from 'react-hook-form';
import css from './index.module.scss';
import SelectLabel from '@components/common/select-label';
import { CURRENCY, WEIGHT_SYSTEM } from '@helpers/constants';
import { TextField } from '@components/common';
import { useEffect } from 'react';
import { IWeightChildOption } from '@helpers/types/quotes';

interface IEnquiryGenericInformationProps {
  setQuoteFormState: any;
  selectedWeightSystemChildOptions: IWeightChildOption[];
}

const InternationalGenericInformation = (props: any) => {
  const { setQuoteFormState, selectedWeightSystemChildOptions, isExpanded } = props;
  const { control, setValue, getValues } = useFormContext();

  const currency1 = useWatch({
    name: `genericInformation.currency1`,
    control
  });

  const currency2 = useWatch({
    name: `genericInformation.currency2`,
    control
  });

  const weightSystem = useWatch({
    name: `genericInformation.weightSystem`,
    control
  });

  const fxRate = useWatch({
    name: `genericInformation.fxRate`,
    control
  });

  useEffect(() => {
    setQuoteFormState((prevState: any) => {
      if (!currency1 && !currency2) {
        return {
          ...prevState,
          selectedCurrency: CURRENCY
        };
      }
      const items = [currency1, currency2].filter(Boolean);

      const uniqueArray = Array.from(new Set(items.map((item) => JSON.stringify(item)))).map(
        (item) => JSON.parse(item)
      );
      return {
        ...prevState,
        selectedCurrency: uniqueArray
      };
    });
  }, [currency1, currency2]);

  useEffect(() => {
    try {
      setQuoteFormState((prevState: any) => {
        return {
          ...prevState,
          selectedWeightSystemChildOptions: weightSystem?.childOptions
        };
      });

      // Keep a primary unit that already belongs to this weight system (e.g. a prefilled one)
      const primaryUnit = getValues('genericInformation.primaryUnit');
      const isPrimaryUnitInSystem = weightSystem?.childOptions.some(
        (item: IWeightChildOption) => item.value === primaryUnit?.value
      );
      if (isPrimaryUnitInSystem) return;

      setValue(
        'genericInformation.primaryUnit',
        weightSystem?.childOptions.find((item: IWeightChildOption) => item.default === true)
      );
    } catch (error) {
      return;
    }
  }, [weightSystem]);

  return (
    <main>
      {isExpanded && (
        <div className={css.formWrapper}>
          <div className={css.rowWrapper}>
            <Controller
              name={`genericInformation.currency1`}
              control={control}
              render={({ field, fieldState }) => (
                <SelectLabel
                  {...field}
                  options={CURRENCY}
                  required
                  label="Currency 1"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={`genericInformation.currency2`}
              control={control}
              render={({ field, fieldState }) => (
                <SelectLabel
                  {...field}
                  options={CURRENCY}
                  label="Currency 2"
                  isClearable
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div className={css.rowWrapper}>
            <Controller
              name={`genericInformation.fxRate`}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  required={currency1 && currency2}
                  label={
                    <span>
                      Fx Rate
                      {currency1 && currency2 && currency1?.value !== currency2?.value && (
                        <span className={css.fxRate}>
                          (1 {currency1?.value} = {fxRate} {currency2?.value})
                        </span>
                      )}
                    </span>
                  }
                  // "Fx Rate"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={`genericInformation.weightSystem`}
              control={control}
              render={({ field, fieldState }) => (
                <SelectLabel
                  {...field}
                  options={WEIGHT_SYSTEM}
                  required
                  label="Weight System"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div className={css.rowWrapper}>
            <Controller
              name={`genericInformation.primaryUnit`}
              control={control}
              render={({ field, fieldState }) => (
                <SelectLabel
                  {...field}
                  options={selectedWeightSystemChildOptions}
                  required
                  label="Primary Unit"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={`genericInformation.draftDealName`}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Deal Name"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default InternationalGenericInformation;
